import { useRef } from 'react';

// global is set in server html generated in
// api/app/lib/embed/html/build.rb
declare global {
  interface Window {
    ___JANE___?: {
      runtimeConfig: ServerRuntimeConfig;
    };
  }
}

// Runtime config is the type exposed to the rest of the application
export type RuntimeConfig = {
  customProductDetailTitleSuffix?: string; // in frameless embed mode, specifies suffix to be used on product detail page titles (i.e. Sour OG | Resin - Little Green Pot Shop, San Francisco, CA).
  defaultToBaseCanonical?: boolean; // in boost for all, links to the store point at the base url instead of /menu, matching the canonical.
  disableJaneTitleAndDescriptionMeta: boolean; // in frameless embed mode, disables Jane specific page meta data (in menu title and for Open Graph).
  embedConfigId?: number;
  isBoostForAll: boolean;
  isFramelessEmbedMode: boolean;
  isPartnerHosted: boolean;
  partnerHasDescription?: boolean;
  partnerHasTitle?: boolean;
  partnerHostedPath?: string;
  partnerHostedStoreId?: number;
  redirectsToBloomMenuUrl?: string;
  showAgeGate: boolean;
  ssoCredentials: { [id: string]: string };
};

// Title the server leaves in client/apps/boost/index.html when the partner has
// none of their own (see api/app/lib/embed/html/build.rb).
const JANE_PLACEHOLDER_TITLE = 'Jane';

type JaneMetaConfig = Pick<
  RuntimeConfig,
  | 'disableJaneTitleAndDescriptionMeta'
  | 'partnerHasDescription'
  | 'partnerHasTitle'
>;

export const skipJaneTitle = (config: JaneMetaConfig) =>
  Boolean(config.disableJaneTitleAndDescriptionMeta) &&
  config.partnerHasTitle !== false;

export const skipJaneDescription = (config: JaneMetaConfig) =>
  Boolean(config.disableJaneTitleAndDescriptionMeta) &&
  config.partnerHasDescription !== false;

// Server config is how the server sets the config.
export type ServerRuntimeConfig = {
  customProductDetailTitleSuffix?: string;
  defaultToBaseCanonical?: boolean;
  disableJaneTitleAndDescriptionMeta?: boolean;
  embedConfigId: number;
  isBoostForAll?: boolean;
  isFramelessEmbedMode?: boolean;
  partnerHostedPath?: string;
  redirectsToBloomMenuUrl?: string;
  showAgeGate: boolean;
  ssoCredentials: { [id: string]: string };
  storeId: number;
};

// value that will be used if server config is not present
export const defaultRuntimeConfig: RuntimeConfig = {
  disableJaneTitleAndDescriptionMeta: false,
  isBoostForAll: false,
  isFramelessEmbedMode: false,
  isPartnerHosted: false,
  showAgeGate: false,
  ssoCredentials: {},
};

// returns either a runtime config based on a server config,
// or the defaultRuntimeConfig if the server config is not present
export const getRuntimeConfig = (): RuntimeConfig => {
  let currentConfig = getCurrentConfig();

  if (currentConfig) {
    return currentConfig;
  }

  const serverConfig = getServerConfig();

  if (!serverConfig) {
    return defaultRuntimeConfig;
  }

  const partnerMeta = detectPartnerMeta();

  currentConfig = {
    customProductDetailTitleSuffix: serverConfig.customProductDetailTitleSuffix,
    defaultToBaseCanonical: serverConfig.defaultToBaseCanonical || false,
    disableJaneTitleAndDescriptionMeta:
      serverConfig.disableJaneTitleAndDescriptionMeta || false,
    embedConfigId: serverConfig.embedConfigId,
    isBoostForAll: serverConfig.isBoostForAll || false,
    isFramelessEmbedMode: serverConfig.isFramelessEmbedMode || false,
    isPartnerHosted: true,
    partnerHasDescription: partnerMeta.hasDescription,
    partnerHasTitle: partnerMeta.hasTitle,
    partnerHostedPath: serverConfig.partnerHostedPath || '',
    partnerHostedStoreId: serverConfig.storeId,
    redirectsToBloomMenuUrl: serverConfig.redirectsToBloomMenuUrl,
    showAgeGate: serverConfig.showAgeGate,
    ssoCredentials: serverConfig.ssoCredentials,
  };

  setCurrentConfig(currentConfig);

  return currentConfig;
};

// the runtime config is set on the server in
// api/app/lib/embed/html/build.rb
// this config will not map 1:1 with what is used on the client side,
// and will only be present on partner hosted sites.
const getServerConfig = (): ServerRuntimeConfig | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.___JANE___?.runtimeConfig || null;
};

type PartnerMeta = { hasDescription?: boolean; hasTitle?: boolean };

const detectPartnerMeta = (): PartnerMeta => {
  if (typeof document === 'undefined') {
    return {};
  }

  const title = document.title.trim();
  const metas = Array.from(document.querySelectorAll('meta[name]'));
  const hasDescription = metas.some(
    (meta) =>
      !meta.hasAttribute('data-rh') &&
      meta.getAttribute('name')?.trim().toLowerCase() === 'description' &&
      Boolean(meta.getAttribute('content')?.trim())
  );

  return {
    hasDescription,
    hasTitle: title !== '' && title !== JANE_PLACEHOLDER_TITLE,
  };
};

// cache of current config. This value is immutable, and
// will not change without a full page reload.
let currentConfig: RuntimeConfig | null = null;

export const getCurrentConfig = (): RuntimeConfig | null => currentConfig;

export const setCurrentConfig = (config: RuntimeConfig | null) => {
  currentConfig = config;
};

export const useRuntimeConfig = (): RuntimeConfig => {
  const runtimeConfig = useRef<RuntimeConfig | null>(null);
  if (runtimeConfig.current === null) {
    runtimeConfig.current = getRuntimeConfig();
  }
  return runtimeConfig.current;
};
